<template>
  <v-card outlined class="search-result-card mb-3">
    <v-card-title class="py-2 px-4 d-flex align-center">
      <div class="flex-grow-1">
        <div class="text-subtitle-1 font-weight-bold theme-heading">
          {{ result.name }}
        </div>
        <div class="text-caption theme-text-secondary">
          {{ result.currentTitle }} at {{ result.currentCompany }}
        </div>
      </div>
      <v-chip
        v-if="showScores"
        small
        :color="scoreColor"
        text-color="white"
        class="font-weight-bold"
      >
        {{ result.overallScore }}%
      </v-chip>
    </v-card-title>

    <v-divider />

    <v-card-text class="py-3 px-4">
      <div class="d-flex align-center mb-2">
        <v-chip x-small outlined :color="categoryColor" class="mr-2" v-if="displayCategory">
          {{ displayCategory }}
        </v-chip>
        <v-chip x-small outlined color="primary" class="mr-2" v-if="displaySeniority">
          {{ displaySeniority }}
        </v-chip>
        <v-chip x-small outlined color="#8B5CF6" v-if="displayCluster">
          {{ displayCluster }}
        </v-chip>
      </div>

      <div v-if="result.categoryExplanation" class="text-caption theme-text-secondary mb-2">
        {{ result.categoryExplanation }}
      </div>

      <div class="mb-2" v-if="topSkills.length > 0">
        <div class="text-caption theme-text-secondary mb-1">Key Skills</div>
        <v-chip
          v-for="skill in topSkills"
          :key="skill"
          x-small
          :color="$vuetify.theme.dark ? '#1A365D' : '#E3F2FD'"
          :text-color="$vuetify.theme.dark ? '#90CAF9' : '#007AFF'"
          class="mr-1 mb-1"
        >
          {{ skill }}
        </v-chip>
      </div>

      <div v-if="fitSignals.length > 0" class="mb-2">
        <div v-for="(signal, sIdx) in fitSignals" :key="sIdx" class="d-flex align-center mb-1">
          <v-icon x-small color="#10B981" class="mr-1">mdi-check-circle</v-icon>
          <span class="text-caption">{{ signal }}</span>
        </div>
      </div>

      <v-expansion-panels flat accordion v-if="hasEvidence">
        <v-expansion-panel class="evidence-panel">
          <v-expansion-panel-header class="px-0 py-1">
            <span class="text-caption font-weight-bold" style="color: #007AFF">
              Evidence Analysis
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-if="facetMatchList.length > 0" class="mb-3">
              <div class="text-caption theme-text-secondary mb-2">Facet Match Breakdown</div>
              <div
                v-for="(fm, idx) in facetMatchList"
                :key="fm.name || idx"
                class="mb-2"
              >
                <div class="d-flex align-center justify-space-between mb-1">
                  <span class="text-caption font-weight-medium">{{ fm.name }}</span>
                  <span class="text-caption font-weight-bold" :style="{ color: getFacetColor(fm.score) }">{{ fm.score }}%</span>
                </div>
                <v-progress-linear
                  :value="fm.score"
                  :color="getFacetColor(fm.score)"
                  :background-color="$vuetify.theme.dark ? '#333' : '#E0E0E0'"
                  height="4"
                  rounded
                />
                <div v-if="hasStarEvidence(fm)" class="star-evidence mt-1 pa-2">
                  <div v-if="fm.situation" class="text-caption mb-1">
                    <span class="font-weight-bold" style="color: #6366F1">Situation:</span>
                    <span class="ml-1">{{ truncate(fm.situation, 200) }}</span>
                  </div>
                  <div v-if="fm.task" class="text-caption mb-1">
                    <span class="font-weight-bold" style="color: #6366F1">Task:</span>
                    <span class="ml-1">{{ truncate(fm.task, 200) }}</span>
                  </div>
                  <div v-if="fm.action" class="text-caption mb-1">
                    <span class="font-weight-bold" style="color: #6366F1">Action:</span>
                    <span class="ml-1">{{ truncate(fm.action, 200) }}</span>
                  </div>
                  <div v-if="fm.result" class="text-caption">
                    <span class="font-weight-bold" style="color: #10B981">Result:</span>
                    <span class="ml-1">{{ truncate(fm.result, 200) }}</span>
                  </div>
                </div>
                <div v-else-if="fm.evidence" class="text-caption theme-text-secondary mt-1" style="font-style: italic">
                  "{{ truncate(fm.evidence, 120) }}"
                </div>
              </div>
            </div>

            <div v-if="result.strongestSignal" class="mb-3">
              <div class="text-caption theme-text-secondary mb-1">Strongest Signal</div>
              <div class="text-caption font-weight-bold" style="color: #10B981">{{ result.strongestSignal }}</div>
            </div>

            <div v-if="evidenceBacking.length > 0" class="mt-3">
              <div class="text-caption font-weight-bold mb-2" style="color: #007AFF">
                <v-icon x-small color="#007AFF" class="mr-1">mdi-shield-check</v-icon>
                Evidence
              </div>
              <div
                v-for="(eb, ebIdx) in evidenceBacking"
                :key="'eb-' + ebIdx"
                class="evidence-card mb-3 pa-3"
              >
                <div class="text-overline theme-text-secondary mb-1">FROM: ACHIEVEMENT</div>
                <div class="text-body-2 mb-2">
                  {{ eb.achievement.action }}
                  <span v-if="eb.achievement.result" class="font-weight-bold"> {{ eb.achievement.result }}</span>
                </div>
                <v-chip x-small :color="getFacetColor(eb.relevanceScore)" text-color="white">
                  {{ eb.facetName }} {{ eb.relevanceScore }}%
                </v-chip>

                <v-expansion-panels flat accordion class="mt-2">
                  <v-expansion-panel class="star-detail-panel">
                    <v-expansion-panel-header class="px-0 py-0">
                      <div class="d-flex align-center">
                        <v-avatar v-for="letter in ['S','T','A','R']" :key="letter" size="20" :color="starColor(letter)" class="mr-1">
                          <span class="white--text" style="font-size: 10px; font-weight: bold">{{ letter }}</span>
                        </v-avatar>
                        <span class="text-caption ml-2">Full STAR breakdown</span>
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="pt-2">
                      <div v-if="eb.achievement.situation" class="mb-2">
                        <v-chip x-small color="#3B82F6" text-color="white" class="mr-1 mb-1">S</v-chip>
                        <span class="text-caption"><strong>Situation:</strong> {{ eb.achievement.situation }}</span>
                      </div>
                      <div v-if="eb.achievement.task" class="mb-2">
                        <v-chip x-small color="#8B5CF6" text-color="white" class="mr-1 mb-1">T</v-chip>
                        <span class="text-caption"><strong>Task:</strong> {{ eb.achievement.task }}</span>
                      </div>
                      <div v-if="eb.achievement.action" class="mb-2">
                        <v-chip x-small color="#F59E0B" text-color="white" class="mr-1 mb-1">A</v-chip>
                        <span class="text-caption"><strong>Action:</strong> {{ eb.achievement.action }}</span>
                      </div>
                      <div v-if="eb.achievement.result" class="mb-2">
                        <v-chip x-small color="#EF4444" text-color="white" class="mr-1 mb-1">R</v-chip>
                        <span class="text-caption"><strong>Result:</strong> {{ eb.achievement.result }}</span>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>

    <v-card-actions class="px-4 py-2">
      <v-spacer />
      <v-btn small text color="#007AFF" @click="$emit('view-profile', result)">
        <v-icon small left>mdi-account-eye</v-icon>
        View Profile
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: "SearchResultCard",
  props: {
    result: {
      type: Object,
      required: true,
    },
    showScores: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    scoreColor() {
      if (this.result.overallScore >= 70) return "#10B981";
      if (this.result.overallScore >= 50) return "#F59E0B";
      return "#9E9E9E";
    },
    displayCategory() {
      var cat = (this.result.matchCategory || "").trim();
      var hidden = ["unknown", "none", "n/a", "undefined", "potential match", ""];
      if (hidden.indexOf(cat.toLowerCase()) >= 0) return "";
      return cat;
    },
    displaySeniority() {
      var sen = (this.result.seniorityLevel || "").trim();
      var hidden = ["unknown", "none", "n/a", "undefined", "mid", "mid-level", ""];
      if (hidden.indexOf(sen.toLowerCase()) >= 0) return "";
      var labels = {
        "junior": "Junior",
        "senior": "Senior",
        "staff": "Staff",
        "principal": "Principal",
        "director": "Director",
        "vp": "VP",
        "c-suite": "C-Suite",
        "executive": "Executive",
        "manager": "Manager",
      };
      return labels[sen.toLowerCase()] || sen;
    },
    displayCluster() {
      var cluster = this.result.specialistCluster || "";
      var hidden = ["unknown", "none", "n/a", "undefined", "general", "other", ""];
      if (hidden.indexOf(cluster.toLowerCase()) >= 0) return "";
      return cluster;
    },
    categoryColor() {
      var colors = {
        "Strong Match": "#10B981",
        "Best Match": "#10B981",
        "Good Match": "#007AFF",
        "Match": "#007AFF",
        "Partial Match": "#F59E0B",
        "Potential Fit": "#F59E0B",
        "Weak Match": "#9E9E9E",
        "Consider": "#9E9E9E",
      };
      return colors[this.displayCategory] || "#007AFF";
    },
    topSkills() {
      if (!this.result.keySkills) return [];
      return this.result.keySkills.slice(0, 5);
    },
    fitSignals() {
      return (this.result.fitSignals || this.result.matchReasons || []).slice(0, 3);
    },
    facetMatchList() {
      var fm = this.result.facetMatches;
      if (!fm) return [];
      if (Array.isArray(fm)) {
        return fm.map(function (f) {
          return {
            name: f.facetName || f.facet_name || f.name || "",
            score: f.relevanceScore || f.score || f.relevance_score || 0,
            evidence: f.evidenceSnippet || f.evidence || f.evidenceSummary || "",
            situation: f.situation || "",
            task: f.task || "",
            action: f.action || "",
            result: f.result || "",
          };
        });
      }
      return Object.entries(fm).map(function ([key, val]) {
        var isObj = typeof val === "object" && val !== null;
        return {
          name: key,
          score: typeof val === "number" ? val : (isObj ? (val.score || val.relevanceScore || 0) : 0),
          evidence: isObj ? (val.evidenceSnippet || val.evidence || val.evidenceSummary || "") : "",
          situation: isObj ? (val.situation || "") : "",
          task: isObj ? (val.task || "") : "",
          action: isObj ? (val.action || "") : "",
          result: isObj ? (val.result || "") : "",
        };
      });
    },
    evidenceBacking() {
      return this.result.evidenceBacking || [];
    },
    hasEvidence() {
      return this.facetMatchList.length > 0 || this.result.strongestSignal || this.evidenceBacking.length > 0;
    },
  },
  methods: {
    getFacetColor(value) {
      if (value >= 70) return "#10B981";
      if (value >= 50) return "#F59E0B";
      return "#EF4444";
    },
    truncate(text, maxLen) {
      if (!text) return "";
      return text.length > maxLen ? text.substring(0, maxLen) + "..." : text;
    },
    starColor(letter) {
      var colors = { S: "#3B82F6", T: "#8B5CF6", A: "#F59E0B", R: "#EF4444" };
      return colors[letter] || "#007AFF";
    },
    hasStarEvidence(facet) {
      if (!facet) return false;
      return !!(facet.situation || facet.task || facet.action || facet.result);
    },
  },
};
</script>

<style scoped>
.star-evidence {
  background: rgba(99, 102, 241, 0.06);
  border-left: 3px solid #6366F1;
  border-radius: 4px;
}
.theme--dark .star-evidence {
  background: rgba(99, 102, 241, 0.12);
}
.search-result-card {
  transition: box-shadow 0.2s ease;
}
.search-result-card:hover {
  box-shadow: 0 2px 8px rgba(30, 58, 95, 0.15);
}
.evidence-card {
  border-left: 3px solid #007AFF;
  border-radius: 6px;
}
.theme-body-dark .evidence-card,
.v-application.theme--dark .evidence-card {
  background: rgba(30, 58, 95, 0.2);
}
.v-application.theme--light .evidence-card {
  background: #F8FAFC;
}
.evidence-panel,
.star-detail-panel {
  background: transparent !important;
}
.evidence-panel::before,
.star-detail-panel::before {
  box-shadow: none !important;
}
.theme-text-secondary {
  color: #666;
}
.v-application.theme--dark .theme-text-secondary {
  color: #aaa;
}
</style>
